import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Calendar, Lock, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useBrandBranding } from "@/hooks/use-brand-branding";
import BrandLogo from "@/components/brand/BrandLogo";
import { useAccountStatus } from "@/hooks/use-account-status";
import { useActivationAccess } from "@/hooks/use-activation-access";
import NextSteps from "@/components/brand/NextSteps";
import { T } from "@/components/brand/profile-type";
import { Eyebrow } from "@/components/brand/profile-ui";
import { openExternalLink } from "@/lib/open-external-link";


interface BrandRecapModalProps {
  open: boolean;
  onClose: () => void;
  /** Render in the page's content area instead of as a full-screen overlay, so the sidebar stays usable. */
  inline?: boolean;
}

const CAL_URL = "https://cal.com/marguerite-japy-u27fez/discovery-call";

// Dummy match data — structured so it can be swapped for a real AI-matching call later.
interface CommunityMatch {
  id: string;
  name: string;
  city: string;
  country: string;
  fitPct: number; // 100 for top match
  description: string;
  irlPerYear: number;
  events: number;
  social: string; // e.g. "12K"
  audienceTags: string[];
  image: string;
}

const DUMMY_MATCHES: CommunityMatch[] = [
  {
    id: "m1",
    name: "Paris Run Crew",
    city: "Paris",
    country: "France",
    fitPct: 100,
    description: "Inclusive running collective hosting weekly social runs and seasonal city takeovers.",
    irlPerYear: 48,
    events: 6,
    social: "18K",
    audienceTags: ["Mixed", "Age 25-40"],
    image: "https://images.unsplash.com/photo-1552674605-db6ffd4facb5?auto=format&fit=crop&w=400&q=70",
  },
  {
    id: "m2",
    name: "Atelier Nocturne",
    city: "Lyon",
    country: "France",
    fitPct: 88,
    description: "Creative night-time community gathering designers, illustrators and indie founders.",
    irlPerYear: 24,
    events: 4,
    social: "9.4K",
    audienceTags: ["Mixed", "Age 22-35"],
    image: "https://images.unsplash.com/photo-1515169067868-5387ec356754?auto=format&fit=crop&w=400&q=70",
  },
  {
    id: "m3",
    name: "Maison Brunch Club",
    city: "Marseille",
    country: "France",
    fitPct: 76,
    description: "Hospitality-led food community curating intimate brunches and chef collabs.",
    irlPerYear: 36,
    events: 5,
    social: "12K",
    audienceTags: ["Skew female", "Age 28-45"],
    image: "https://images.unsplash.com/photo-1529543544282-ea669407fca3?auto=format&fit=crop&w=400&q=70",
  },
];



const BrandRecapModal = ({ open, onClose, inline = false }: BrandRecapModalProps) => {
  const { data: branding, isLoading: brandingLoading } = useBrandBranding();
  const { status, loading: statusLoading } = useAccountStatus();
  const navigate = useNavigate();
  const { families } = useActivationAccess();

  const isAccepted = status === "accepted";

  const name = branding?.brandName || "your brand";
  const description =
    branding?.analysis?.positioning_sentence ||
    branding?.summary ||
    "We found the first brand signals from your site and onboarding answers.";
  const territory = branding?.analysis?.cultural_territory_pair || branding?.analysis?.cultural_territory || null;
  const subCategory = branding?.analysis?.sub_category || null;

  const intelligenceCards = [
    subCategory && { key: "category", label: "Product category", value: subCategory, kind: "text" as const },
    territory && { key: "territory", label: "Cultural territory", value: territory, kind: "text" as const },
  ].filter(Boolean) as Array<{ key: string; label: string; value: string | string[]; kind: "text" | "chips" }>;

  const gridColsClass =
    intelligenceCards.length >= 3 ? "sm:grid-cols-3" : intelligenceCards.length === 2 ? "sm:grid-cols-2" : "sm:grid-cols-1";

  if (!open) return null;

  // Inline (the community pages): send the brand to briefing. Overlay: just dismiss it.
  const handlePrimary = () => {
    if (inline) {
      navigate(`/activate/${families[0] ?? "product"}`);
      return;
    }
    onClose();
  };
  // The brand block and its cards open the brand profile.
  const goToBrand = () => {
    onClose();
    navigate("/brand");
  };
  const brandLinkProps = {
    role: "link" as const,
    tabIndex: 0,
    title: "View your brand profile",
    onClick: goToBrand,
    onKeyDown: (e: React.KeyboardEvent) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        goToBrand();
      }
    },
  };
  const LINK_BOX = "cursor-pointer transition-colors hover:border-foreground/40 hover:bg-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";
  const primaryLabel = inline ? "Brief ernest" : isAccepted ? "Access ernest" : "Continue";

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.25 }}
        className={inline ? "w-full" : "fixed inset-0 z-[100] flex items-center justify-center bg-foreground/20 backdrop-blur-[3px] px-5 py-6"}
      >
        <motion.div
          initial={{ opacity: 0, y: 18, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 10, scale: 0.98 }}
          transition={{ duration: 0.32, ease: "easeOut" }}
          className={`relative w-full max-w-[720px] max-h-[90vh] overflow-hidden rounded-2xl border border-border bg-card text-foreground shadow-2xl ${inline ? "mx-auto" : ""}`}
        >
          <div className="max-h-[90vh] overflow-y-auto">
            <div className="relative overflow-hidden px-7 pb-5 pt-6">
              {/* The empty-state banners' soft glow, with the colors swapped: indigo top left, lime bottom right */}
              <div
                aria-hidden
                className="absolute inset-0 opacity-90 pointer-events-none"
                style={{
                  background:
                    "radial-gradient(60% 80% at 20% 20%, rgba(123,111,240,0.14), transparent 60%), radial-gradient(50% 60% at 90% 90%, rgba(222,255,0,0.20), transparent 60%)",
                }}
              />
              <div className="relative">
                {!statusLoading && !isAccepted && (
                  <div className="mb-3 flex items-center gap-3 rounded-xl border border-[#deff00] bg-[#f5ffa3]/60 px-4 py-3">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#DEFF00]">
                      <Lock className="block h-[18px] w-[18px] text-foreground" />
                    </span>
                    <div>
                      <p className="text-[16px] font-semibold leading-snug text-foreground">
                        Community data opens once our team has verified your account.
                      </p>
                      <p className="mt-0.5 text-[15px] text-foreground/70">
                        Until then, you can set up your brand and send briefs.
                      </p>
                    </div>
                  </div>
                )}
                {brandingLoading ? (
                  <div className="flex items-center gap-4 rounded-2xl border border-border bg-background/70 p-3.5" aria-hidden>
                    <Skeleton className="h-[60px] w-[60px] shrink-0 rounded-lg" />
                    <div className="min-w-0 flex-1 space-y-1.5">
                      <Skeleton className="h-6 w-1/3" />
                      <Skeleton className="h-4 w-full" />
                      <Skeleton className="h-4 w-2/3" />
                    </div>
                  </div>
                ) : (
                <div {...brandLinkProps} className={`flex items-center gap-4 rounded-2xl border border-border bg-background/70 p-3.5 ${LINK_BOX}`}>
                  <BrandLogo src={branding?.logo} name={branding?.brandName} size={60} rounded="lg" primaryColor={branding?.primaryColor} />
                  <div className="min-w-0 flex-1">
                    <h3 className="truncate text-[22px] font-semibold leading-tight tracking-[-0.01em] text-foreground">
                      {name}
                    </h3>
                    <p className="mt-0.5 text-[15px] leading-snug text-foreground/70 line-clamp-3">{description}</p>
                  </div>
                </div>
                )}

                {intelligenceCards.length > 0 && (
                  <div className={`mt-3 grid gap-3 ${gridColsClass}`}>
                    {intelligenceCards.map((card) => (
                      <div key={card.key} {...brandLinkProps} className={`rounded-2xl border border-border bg-background/70 px-4 py-3 ${LINK_BOX}`}>
                        <Eyebrow>{card.label}</Eyebrow>
                        {card.kind === "text" ? (
                          <p className={`mt-2 ${T.keyStrong}`}>{card.value as string}</p>
                        ) : (
                          <div className="mt-2 flex flex-wrap gap-1.5">
                            {(card.value as string[]).map((c) => (
                              <span
                                key={c}
                                className="rounded-full border border-foreground/15 bg-background px-2.5 py-1 text-[11px] font-medium text-foreground/80"
                              >
                                {c}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>


            {/* CTAs */}
            <section className="border-t border-border bg-[#FAFAF8] px-6 pb-5 pt-3 sm:px-7">
              {statusLoading ? (
                <div className="flex justify-center" aria-hidden>
                  <Skeleton className="h-12 w-48 rounded-xl" />
                </div>
              ) : isAccepted ? (
                <div className="flex flex-col sm:flex-row gap-3 sm:justify-center">
                  {/* Same two looks and hover as the landing page CTAs: a soft violet secondary, and the neon primary. */}
                  <Button
                    onClick={() => openExternalLink(CAL_URL)}
                    className="h-12 rounded-xl border border-[#5b4fff]/15 bg-transparent bg-gradient-to-r from-[#5b4fff]/10 to-[#8b7fff]/[0.06] px-6 text-[15px] font-medium text-[#5b4fff] shadow-none transition hover:scale-[1.02] hover:bg-transparent hover:from-[#5b4fff]/15 hover:to-[#8b7fff]/10 active:scale-100"
                  >
                    <Calendar className="mr-2 h-4 w-4" />
                    Schedule your onboarding
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                  <Button
                    onClick={handlePrimary}
                    className="h-12 rounded-xl border border-black/10 bg-[hsl(var(--ernest-neon))] px-7 text-[15px] font-medium text-foreground shadow-none transition hover:scale-[1.02] hover:bg-[hsl(var(--ernest-neon))] active:scale-100"
                  >
                    {primaryLabel}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <>
                  <NextSteps onNavigate={onClose} />
                  {!inline && (
                    <div className="mt-4 flex justify-center">
                      <Button variant="ghost" onClick={onClose} className="text-sm text-muted-foreground hover:text-foreground">Continue to ernest</Button>
                    </div>
                  )}
                </>
              )}
              <p className="mt-3.5 text-center text-[12px] text-muted-foreground">
                Not your logo or details?{" "}
                <button
                  onClick={() => {
                    const subject = encodeURIComponent("Onboarding issue — brand info isn't mine");
                    const body = encodeURIComponent(
                      `Hi ernest team,\n\nI just onboarded but the brand information you populated for me isn't correct (logo, description, category or competitors are off).\n\nCould you help me fix this?\n\nThanks.`
                    );
                    window.location.href = `mailto:hello@ernest-app.com?subject=${subject}&body=${body}`;
                  }}
                  className="inline-flex items-center gap-1 font-medium text-foreground underline decoration-foreground/40 underline-offset-2 transition-colors hover:text-[#5b4fff] hover:decoration-[#5b4fff]/40"
                >
                  Contact support
                </button>
              </p>

            </section>
          </div>

        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default BrandRecapModal;
